import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { Box } from '@mui/material';
import { generateHexaRandomColor } from '../../../../utils.js';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';

const PAGE_HEIGHT = 1097;
const TITLE_HEIGHT = 28;
const FULL_CONTENT_HEIGHT = 908;
const LIGHT_CONTENT_HEIGHT = 886;

export const LocationsCalendarPdfComponent = forwardRef(function LocationsCalendarPdfComponent(props, ref) {
    const {
        locations,
        nbPeriodes,
        hideProducts,
        hideNonAttribues,
        debugDom,
        titleInfos
    } = props;

    const [pages, setPages] = useState(1);
    const [mounted, setMounted] = useState(0);
    const [preRendered, setPreRendered] = useState(false);
    const [rendered, setRendered] = useState(false);
    const [resources, setResources] = useState([locations]);

    const calendarRef = useRef([]);

    useEffect(() => {
        if (mounted === 1 && !preRendered) filterResources();
        if (mounted > 0 && rendered) props.callback();
    }, [mounted, rendered])

    useEffect(() => {
        resizeTimelineFrame();
    }, [resources, pages])

    const filterResources = () => {
        let filteredResources = [];
        [...calendarRef?.current ?? []].map((ref, i) => {
            let calendarResources = [];
            let calendarApi = ref.getApi();
            let calendarRows = calendarApi.el.querySelectorAll('.fc-timeline-lane-frame');
            [...calendarRows].map((row) => {
                const resourceId = row.closest('td').dataset.resourceId;
                const resource = resources[i].filter((r) => r.id == resourceId).shift();
                // Masquage des évènements non attribués
                if (!hideNonAttribues || (hideNonAttribues && resource.id !== 0)) {
                    resource.offsetHeight = row.offsetHeight;
                    calendarResources.push(resource);
                }
            })
            filteredResources[i] = calendarResources;
        })
        calcPages(filteredResources);
    }

    const calcPages = (filteredResources) => {
        let index = 0;
        let calendarApi = calendarRef?.current?.[0]?.getApi();
        if (calendarApi) {
            let calendarResources = [];
            let timelineHeader = calendarApi.el.querySelector('.fc-scrollgrid-section-header').lastChild.querySelector('.fc-scroller');
            let pageHeigh = timelineHeader.offsetHeight;

            filteredResources[index].map((resource) => {
                if (!calendarResources[index]) calendarResources[index] = [];
                pageHeigh += resource.offsetHeight ?? 0;
                if (pageHeigh > (hideProducts ? LIGHT_CONTENT_HEIGHT : FULL_CONTENT_HEIGHT)) {
                    index = index + 1;
                    pageHeigh = timelineHeader.offsetHeight + (resource.offsetHeight ?? 0);
                }
                calendarResources[index] = [...(calendarResources[index] ?? []), resource];
            })
            setResources(calendarResources);
            setPages(calendarResources.length);
            setPreRendered(true);
        }
    }

    const resizeTimelineFrame = () => {
        [...calendarRef?.current ?? []].map((ref, i) => {
            let calendarApi = ref.getApi();
            let calendarRows = calendarApi.el.querySelectorAll('.fc-timeline-lane-frame');
            [...calendarRows].map((row) => {
                const resourceId = row.closest('td').dataset.resourceId;
                const resourceSlot = calendarApi.el.querySelector(`.fc-datagrid-cell.fc-resource[data-resource-id="${resourceId}"]`);
                const eventRow = row.querySelector('.fc-timeline-events.fc-scrollgrid-sync-inner');
                const eventSlots = row.querySelectorAll('.fc-timeline-event-harness');
                if (eventSlots.length) {
                    setTimeout(() => row.style.height = resourceSlot.querySelector('.fc-datagrid-cell-frame').style.height = eventRow.style.height, 100);
                } else {
                    row.style.height = resourceSlot.querySelector('.fc-datagrid-cell-frame').style.height = hideProducts ? '48px' : '60px';
                }
            })
            setTimeout(() => setMounted(i + 1), 100);
        });
        if (preRendered) {
            setTimeout(() => setRendered(true), 1500);
        }
    }

    return (
        <Wrapper
            ref={ref}
            className={`nb-periodes${nbPeriodes}`}
            hideproducts={hideProducts.toString()}
        >
            {[...Array(pages).keys()].map((_, i) => (
                <Page key={i} sx={{ background: debugDom ? generateHexaRandomColor() : 'initial' }}>
                    <Title className='text-gc-dark-blue font-weight-bold'>
                        <span>{titleInfos?.hotel}</span>
                        <span>{titleInfos?.datesPeriod}</span>
                    </Title>
                    <FullCalendar
                        {...props}
                        ref={el => calendarRef.current[i] = el}
                        slotMinWidth={45}
                        plugins={[resourceTimelinePlugin]}
                        headerToolbar={{ left: '', right: '' }}
                        locale={ENV.lang.substring(0, 2)}
                        height="auto"
                        resources={resources[i]}
                        resourceOrder="groupId,position,id"
                        resourceAreaWidth="100px"
                        resourceAreaColumns={[{ headerContent: '' }]}
                    />
                </Page>
            ))}
        </Wrapper>
    );
});

const Wrapper = styled.div`
    width: 1500px;

    @media print {
        @page {
            size: landscape;
            margin: 6mm;
        }
        html, body {
            height: 100vh;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
        }
    }

    .fc-datagrid-cell-frame {
        min-height: ${props => props.hideproducts ? 48 : 60}px;
    }

    .fc-resource,
    .fc-datagrid-cell-main {
        font-size: 12px !important;
        line-height: 12px;

        span {
            font-size: 10px;
        }
    }

    .fc-semaine-view,
    .fc-cinqjours-view,
    .fc-resourceTimelineDay-view {
        .fc-timeline-slot-cushion {
            display: flex;
            align-items: center;

            .fc-slot-label-jour {
                margin-right: 8px;
            }
        }
    }

    .fc-slot {
        font-size: 10px !important;
    }

    .fc-slot-label-periode {
        font-size: 9px !important;
    }

    div[event-id] {
        min-height: ${props => props.hideproducts ? 45 : 60}px !important;
        padding-bottom: 2px;

        .fc-event-card-period {
            padding: 2px 2px 0 2px;

            span {
                font-size: 8px;
            }
        }

        .fc-event-card-title {
            padding: 0 2px;
            font-size: 1em;
        }

        .fc-event-card-meeting-room {
            margin-bottom: ${props => props.hideproducts ? 0 : '8px'};
        }

        .fc-event-card-meeting-room, .fc-event-card-product {
            padding: 2px;
            paddind-top: 0;

            i, span {
                font-size: 9px;
            }

            .fc-event-card-product-label {
                font-size: 9px;
            }
        }
    }
    div[status="lce_bloque"] {
        background: #DFDFDF
    }
`;

const Title = muiStyled(Box)({
    height: TITLE_HEIGHT,
    paddingTop: 12,
    display: 'flex',
    justifyContent: 'space-between'
});

const Page = muiStyled(Box)({
    height: PAGE_HEIGHT
});

export const createPdfRoot = (calendarEl, debugDom = false) => {
    const section = calendarEl.closest('section');
    const container = document.createElement('div');
    container.style.padding = '1rem';
    container.style.background = '#F0F';
    container.style.width = '1532px';
    if (!debugDom) {
        document.querySelector('body').style.overflow = container.style.overflow = 'hidden';
        container.style.marginTop = '200vh';
        section.append(container);
    }
    else section.prepend(container);
    return {
        pdfRoot: createRoot(container),
        container
    };
}

export const unmountPdfRoot = (pdfRoot, container) => {
    document.querySelector('body').style.overflow = 'auto';
    pdfRoot.unmount();
    container.remove();
}